import React, { useEffect } from "react";
import { Mention, MentionsInput } from "react-mentions";
import { useDispatch } from "react-redux";
import { Updater, useImmer } from "use-immer";

import { IgTypography } from "@intelligems/ig-theme";
import {
  DiscountTierDto,
  PartialUserInterfaceDto,
  WidgetDto,
} from "@intelligems/ig-types";
import { Grid } from "@mui/material";

import { setInterfaceTextReplacements } from "../../../../../store/slices/userInterfaces";
import { setProgressBarWidgetTextReplacements } from "../../../../../store/slices/widgets";
import theme from "../../../../../theme";
import {
  fromBuyMoreTemplateReplacements,
  toBuyMoreTemplateReplacements,
} from "../../../utils";

interface TextReplacementsProps {
  widgetInterface?: PartialUserInterfaceDto;
  setWidgetInterface?: Updater<PartialUserInterfaceDto>;
  widget?: WidgetDto;
  setWidget?: Updater<WidgetDto>;
  templateKey: string;
  configType: "mobile" | "desktop";
  headerTitle: string;
  height?: number;
  variableValues: { id: string; display: string }[];
  placeholder?: string;
  discountTier?: DiscountTierDto;
  entityType: string;
}

interface InterfaceTextReplacementsProps {
  widgetInterface: PartialUserInterfaceDto;
  templateKey: string;
  headerTitle: string;
  variableValues: { id: string; display: string }[];
  placeholder?: string;
  discountTier?: DiscountTierDto;
}

interface WidgetTextReplacementsProps {
  widget: WidgetDto;
  setWidget: Updater<WidgetDto>;
  templateKey: string;
  configType: "mobile" | "desktop";
  headerTitle: string;
  variableValues: { id: string; display: string }[];
}

export const DiscountProgressBarTextReplacements = (
  props: Omit<InterfaceTextReplacementsProps, "variableValues">
) => {
  const variableValues: { id: string; display: string }[] = [
    {
      id: "quantity",
      display: "#quantity",
    },
    {
      id: "unitName",
      display: "#unitName",
    },
    {
      id: "more",
      display: "#more",
    },
    {
      id: "discount",
      display: "#discount",
    },
  ];
  const tooltipVariableValues: { id: string; display: string }[] = [
    {
      id: "unitName",
      display: "#unitName",
    },
    {
      id: "more",
      display: "#more",
    },
    {
      id: "discount",
      display: "#discount",
    },
  ];

  return (
    <TextReplacements
      {...props}
      variableValues={
        props.templateKey === "tooltipTemplate"
          ? tooltipVariableValues
          : variableValues
      }
      configType={"desktop"}
      placeholder={props?.placeholder}
      discountTier={props.discountTier}
      entityType={"discountProgressBar"}
    />
  );
};

export const ShippingProgressBarTextReplacements = (
  props: Omit<WidgetTextReplacementsProps, "variableValues" | "setWidget">
) => {
  const variableValues: { id: string; display: string }[] = [
    {
      id: "quantity",
      display: "#quantity",
    },
  ];

  return (
    <TextReplacements
      entityType={"shippingProgressBar"}
      {...props}
      variableValues={variableValues}
      height={60}
    />
  );
};

export const MessageBoxTextReplacements = (
  props: Omit<InterfaceTextReplacementsProps, "variableValues">
) => {
  const variableValues: { id: string; display: string }[] = [];

  return (
    <TextReplacements
      entityType={"discountMessageBox"}
      {...props}
      variableValues={variableValues}
      configType={"desktop"}
    />
  );
};

export const TextReplacements = ({
  widgetInterface,
  widget,
  configType,
  templateKey,
  variableValues,
  headerTitle,
  height,
  placeholder,
  discountTier,
}: TextReplacementsProps) => {
  const dispatch = useDispatch();
  const handleValue = () => {
    if (
      widgetInterface?.widget?.widgetType === "discountProgressBar" &&
      discountTier
    ) {
      return fromBuyMoreTemplateReplacements(
        widgetInterface?.desktopVariables?.textReplacements?.tierVariables?.[
          discountTier.id
        ]?.[templateKey]
          ? widgetInterface?.desktopVariables?.textReplacements?.tierVariables[
              discountTier.id
            ][templateKey]
          : (widget?.config || {})[configType]?.variables?.textReplacements
            ? (widget?.config || {})[configType]?.variables?.textReplacements[
                templateKey
              ]
            : ""
      );
    } else {
      return fromBuyMoreTemplateReplacements(
        widgetInterface?.desktopVariables?.textReplacements[templateKey]
          ? widgetInterface?.desktopVariables?.textReplacements[templateKey]
          : (widget?.config || {})[configType]?.variables?.textReplacements
            ? (widget?.config || {})[configType]?.variables?.textReplacements[
                templateKey
              ]
            : ""
      );
    }
  };

  const [value, setValue] = useImmer(handleValue());

  useEffect(() => {
    setValue(handleValue());
  }, [
    widget?.config?.desktop?.variables?.textReplacements,
    widget?.config?.mobile?.variables?.textReplacements,
    templateKey,
  ]);

  useEffect(() => {
    if (widgetInterface) {
      // will not run for shippingProgressBar
      dispatch(
        setInterfaceTextReplacements({
          widgetType: widgetInterface?.widget?.widgetType,
          discountTier,
          templateKey,
          value,
        })
      );
    } else if (widget) {
      dispatch(
        setProgressBarWidgetTextReplacements({ configType, value, templateKey })
      );
    }
  }, [value]);

  return (
    <Grid item container direction={"column"} rowSpacing={1}>
      <Grid item>
        <IgTypography sx={{ fontWeight: 600, fontSize: "14px" }}>
          {headerTitle}
        </IgTypography>
      </Grid>
      <Grid item>
        <MentionsInput
          autoFocus={false}
          maxLength={templateKey === "title" ? 34 : 92}
          className={"ig-mentions-input"}
          style={{
            control: {
              backgroundColor: "#fff",
              fontSize: 16,
              width: "100%",
              borderRadius: "5px",
            },
            "&multiLine": {
              control: {
                fontFamily: theme.typography.fontFamily,
                minHeight: height || 40,
              },
              highlighter: {
                padding: "8px 9px 8px 9px",
                border: "1px solid transparent",
              },
              input: {
                padding: "8px 9px 8px 9px",
                border: "1px solid #0000003b",
                borderRadius: "5px",
                "&:focus-visible": {
                  border: "1px solid red",
                },
              },
            },
            suggestions: {
              list: {
                backgroundColor: "white",
                border: "1px solid rgba(0,0,0,0.15)",
                fontSize: 16,
                borderRadius: 5,
              },
              item: {
                fontFamily: theme.typography.fontFamily,
                padding: "5px 15px",
                borderBottom: "1px solid rgba(0,0,0,0.15)",
                "&focused": {
                  backgroundColor: "#ECF0FF",
                },
              },
            },
          }}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={
            placeholder
              ? placeholder
              : variableValues.length
                ? "Type your message here and enter # to access the variable list"
                : "Type your message here"
          }
        >
          <Mention
            data={variableValues}
            trigger="#"
            style={{
              position: "relative",
              textShadow:
                "1px 1px 1px white, 1px -1px 1px white, -1px 1px 1px white, -1px -1px 1px white",
              zIndex: 1,
              color: "#2962FF",
              pointerEvents: "none",
            }}
          />
        </MentionsInput>
      </Grid>
    </Grid>
  );
};
